import { CheckOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Button, Card, useTheme } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import * as Sentry from '@sentry/react';
import React, { ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { Amplitude } from '../../amplitude/amplitude';
import {
  decodeToken,
  getApiUrl,
  getAppUrl,
  getEnv,
  getSetUpProfileErrorMessageID,
  validateName,
  validateProfile,
} from '../../utils/utils';
import { AcceptUserInviteBody, ProfileFieldProps } from '../types';
import { COUNTRY_TO_PHONE_MAP } from './ProfileConstants';

/**
 * Displays the first name, last name, country code, and phone number fields.
 */
export const ProfileField = ({
  isCreatingWorkspace,
  profile,
  setProfile,
}: {
  isCreatingWorkspace: boolean;
  profile: ProfileFieldProps['profile'];
  setProfile: React.Dispatch<
    React.SetStateAction<ProfileFieldProps['profile']>
  >;
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const appUrl = getAppUrl();

  const hasValidFirstName = validateName(profile.firstName);
  const hasValidLastName = validateName(profile.lastName);

  // The options for the country code field. The key is the country code and the
  // value is the country code followed by the phone number. Example: ['SG', 'SG
  // (+65)']
  const codeOptions: [string, string][] = Object.entries(COUNTRY_TO_PHONE_MAP)
    .sort((a: [string, string], b: [string, string]) =>
      a[0].localeCompare(b[0]),
    )
    .map(([country, code]) => [country, `${country} (+${code})`]);

  const codeMap = new Map<string, string>(codeOptions);

  // The handler for the first name field.
  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      firstName: event.target.value,
    });
  };

  // The handler for the last name field.
  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      lastName: event.target.value,
    });
  };

  // The handler for the country code field.
  const handleCodeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
  ) => {
    if (value != null) {
      setProfile({
        ...profile,
        country: value.slice(0, 2),
      });
    }
  };

  // The handler for the phone number field. Removes all non-numeric characters.
  const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = ((event.target as HTMLInputElement).value = (
      event.target as HTMLInputElement
    ).value.replace(/\D/g, ''));

    setProfile({
      ...profile,
      phoneNumber,
    });
  };

  // The handler for the terms of use field.
  const handleTermsOfUseChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      termsOfUse: event.target.checked,
    });
  };

  // The handler for the sales and marketing field.
  const handleSalesAndMarketingChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setProfile({
      ...profile,
      salesMarketingConsent: event.target.checked,
    });
  };

  const onClickSetUpProfile = async (): Promise<void> => {
    const { id, databaseID } = decodeToken(profile.token);
    Amplitude.getInstance(id).track('setupProfileComplete', {
      email: profile.email,
      country: profile.country,
      databaseID,
      userType: isCreatingWorkspace ? 'newUser' : 'invitedUser',
    });
    const env = getEnv();
    try {
      const firstName = profile.firstName.trim();
      const lastName = profile.lastName?.trim() ?? '';
      const displayName = [firstName, lastName].filter(Boolean).join(' ')
        .slice(0, 50);
      const phoneCode = COUNTRY_TO_PHONE_MAP[profile.country];

      const body: AcceptUserInviteBody = {
        token: profile.token,
        sso: profile.sso,
        password: profile.password,
        firstName: firstName,
        lastName: lastName,
        displayName: displayName,
        phoneCode: phoneCode,
        phoneNumber: profile.phoneNumber,
        termsOfUse: profile.termsOfUse ? 1 : 0,
        marketingConsent: profile.salesMarketingConsent,
        signature: {
          text: displayName,
          font: 'dancingScript',
        },
      };

      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const responseBody = await response.json();

      let errorCode;
      if (response.status != 201 || responseBody['error'] != null) {
        errorCode =
          response.status == 401 && responseBody['error'] == null
            ? 'DEFAULT'
            : (responseBody['error'] as string);
      }
      if (errorCode) {
        const errorMessage = getSetUpProfileErrorMessageID(
          errorCode,
          location.pathname,
        );
        navigate('/error', {
          replace: true,
          state: {
            profile: profile,
            env,
            errorMessage,
          },
        });
      } else {
        navigate('/success', {
          replace: true,
          state: {
            profile: profile,
            env,
            apiUrl,
            title: intl.formatMessage({ id: 'success.title.profile_created' }),
          },
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage = getSetUpProfileErrorMessageID(
        'DEFAULT',
        location.pathname,
      );
      navigate('/error', {
        replace: true,
        state: {
          profile: profile,
          env,
          errorMessage,
        },
      });
    }
  };

  return (
    <div>
      <TextField
        fullWidth
        required
        sx={{
          marginBottom: '16px',
        }}
        InputLabelProps={{
          style: {
            display: 'flex',
            flexDirection: 'row-reverse',
            gap: '2px',
          },
        }}
        inputProps={{
          maxLength: 50,
        }}
        id="first-name"
        label={intl.formatMessage({ id: 'first_name' })}
        value={profile.firstName}
        onChange={handleFirstNameChange}
        error={!hasValidFirstName}
        helperText={
          hasValidFirstName
            ? ''
            : intl.formatMessage({ id: 'first_name.error' })
        }
      ></TextField>
      <TextField
        fullWidth
        sx={{
          marginBottom: '16px',
        }}
        inputProps={{
          maxLength: 50,
        }}
        id="last-name"
        label={intl.formatMessage({ id: 'last_name' })}
        value={profile.lastName}
        onChange={handleLastNameChange}
        error={!hasValidLastName}
        helperText={
          hasValidLastName ? '' : intl.formatMessage({ id: 'last_name.error' })
        }
      ></TextField>
      <Box sx={{ display: 'flex', maxWidth: '100%' }}>
        <Autocomplete
          disableClearable={!!profile.country}
          sx={{
            flex: 1.5,
            marginBottom: '8px',
            marginRight: '8px',
          }}
          id="code"
          options={Array.from(codeMap.values())}
          value={codeMap.get(profile.country) ?? null}
          renderOption={(props, option) => (
            <Box component="li" sx={{ gap: '8px' }} {...props}>
              <CheckOutlined
                style={{
                  color:
                    codeMap.get(profile.country) == option
                      ? theme.palette.success.main
                      : 'transparent',
                }}
              />
              {option}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={intl.formatMessage({ id: 'code' })}
              required
              InputLabelProps={{
                style: {
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  gap: '2px',
                },
              }}
            />
          )}
          ListboxProps={{ style: { maxHeight: 175 } }}
          onChange={handleCodeChange}
        ></Autocomplete>
        <TextField
          required
          sx={{
            flex: 2,
            marginBottom: '8px',
          }}
          InputLabelProps={{
            style: {
              display: 'flex',
              flexDirection: 'row-reverse',
              gap: '2px',
            },
          }}
          inputProps={{
            onInput: handlePhoneNumberChange,
            maxLength: 15,
          }}
          id="phone-number"
          label={intl.formatMessage({ id: 'phone_number' })}
          value={profile.phoneNumber}
        ></TextField>
      </Box>
      <Card
        sx={{
          padding: '8px',
          marginBottom: '8px',
          maxWidth: 362,
        }}
        elevation={0}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={profile.termsOfUse}
                onChange={handleTermsOfUseChange}
              />
            }
            label={intl.formatMessage(
              { id: 'terms_of_service.agree_to' },
              {
                url: (
                  <a
                    href={`${appUrl}/terms-of-use`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                    }}
                  >
                    {intl.formatMessage({
                      id: 'terms_of_service.terms_of_use',
                    })}
                  </a>
                ),
              },
            )}
          />
        </FormGroup>
      </Card>
      <Card
        sx={{
          padding: '8px',
          marginBottom: '16px',
          maxWidth: '100%',
        }}
        elevation={0}
      >
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={profile.salesMarketingConsent}
                onChange={handleSalesAndMarketingChange}
              />
            }
            label={intl.formatMessage({ id: 'marketing_agreement' })}
          />
        </FormGroup>
      </Card>
      <Card elevation={0}>
        <div>
          <Button
            fullWidth
            disableElevation
            color="success"
            disabled={!validateProfile(profile)}
            variant="contained"
            sx={{
              textTransform: 'capitalize',
              '&.Mui-disabled': {
                background: '#7EC141',
              },
            }}
            onClick={() => {
              onClickSetUpProfile();
            }}
          >
            {intl.formatMessage({
              id: 'set_up_profile',
            })}
          </Button>
        </div>
      </Card>
    </div>
  );
};
